import React, { useContext, useState } from 'react';

import { Info, Sparkles, ToyBrick } from 'lucide-react';

import Colors from 'common/colors/constants';
import Tooltip from 'common/common/Tooltip';
import { ActiveIntegrationContext } from 'common/containers/ActiveIntegrationsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import UpsellModal from 'common/modals/UpsellModal';
import ButtonV2 from 'common/ui/ButtonV2';
import Card from 'common/ui/Card';
import LoadingBar from 'common/ui/LoadingBar';
import { P } from 'common/ui/Text';
import getPlanNameForTrial from 'common/util/getPlanNameForTrial';
import isFree, { isFreeV4 } from 'common/util/isFree';
import nbspLastSpace from 'common/util/nbspLastSpace';
import { getEnabledSyncIntegrations } from 'common/util/syncIntegrations';
import styles from 'css-module/components/subdomain/admin/settings/AdminIntegrationSettings/_AdminIntegrationPlanCard.module.scss';

import type { Company } from 'common/api/endpoints/companies';

const IntegrationCount = ({
  integrationCount,
  integrationLimit,
}: {
  integrationCount: number;
  integrationLimit: number | null;
}) => {
  if (integrationLimit) {
    return (
      <div className={styles.integrationCount}>
        <P fontWeight="medium" className={styles.integrationCountText}>
          <ToyBrick
            aria-hidden
            className={styles.icon}
            size={14}
            strokeWidth={2}
            color={Colors.gray120}
          />
          {integrationCount} / {integrationLimit}
          <span className={styles.countSubtext}>Integrations</span>
        </P>
        <LoadingBar percentage={(integrationCount / integrationLimit) * 100} />
      </div>
    );
  }

  return (
    <div className={styles.integrationCount}>
      <P fontWeight="medium" className={styles.integrationCountText}>
        <ToyBrick
          aria-hidden
          className={styles.icon}
          size={14}
          strokeWidth={2}
          color={Colors.gray120}
        />
        {integrationCount} / ∞ <span className={styles.countSubtext}>Integrations</span>
      </P>
    </div>
  );
};

enum Modal {
  upsell = 'upsell',
}

const AdminIntegrationPlanCard = () => {
  const { integrationCount, integrationLimit } = useContext(ActiveIntegrationContext);
  const company = useContext<Company>(CompanyContext);

  const enabledNonAPIDataSyncs = getEnabledSyncIntegrations(company.installedSyncIntegrations);

  let planName = company.trialingPlan
    ? `${getPlanNameForTrial(company.trialingPlan)} (trial)`
    : company.billingData?.plan?.name;

  if (!planName) {
    planName = 'No plan';
  }

  const [modal, setModal] = useState<Modal | null>(null);
  const planID = company.billingData?.plan?.planID;

  // If on old free plan, show "more". Every other upgrade flow gets "unlimited"
  const amount = isFree(planID) && !isFreeV4(planID) ? 'More' : 'Unlimited';

  const planToolTip = `Your plan gets ${integrationLimit ?? 'unlimited'} ${
    integrationLimit !== 1 ? 'integrations' : 'integration'
  }, and unlimited sources`;

  return (
    <Card borderStyle="solid" className={styles.card}>
      <div className={styles.planId}>
        <P fontWeight="medium">{planName}</P>
        <Tooltip
          containerClassName={styles.tooltipContainer}
          value={planToolTip}
          className={styles.tooltip}>
          <Info size={14} strokeWidth={2} color={Colors.gray90} />
        </Tooltip>
      </div>
      <ul>
        <li>
          <IntegrationCount
            integrationCount={integrationCount}
            integrationLimit={integrationLimit}
          />
        </li>
        <li>
          <P fontWeight="medium" className={styles.dataSyncCount}>
            <Sparkles
              aria-hidden
              className={styles.icon}
              size={14}
              strokeWidth={2}
              color={Colors.gray120}
            />
            {enabledNonAPIDataSyncs.length} / ∞ <span className={styles.countSubtext}>Sources</span>
          </P>
        </li>
      </ul>
      {integrationLimit && (
        <ButtonV2
          size="medium"
          variant="outlined"
          className={styles.upgradeButton}
          onClick={() => setModal(Modal.upsell)}>
          Upgrade Plan
        </ButtonV2>
      )}
      {modal && (
        <UpsellModal
          cta={nbspLastSpace(`${amount} integrations to suit your workflow`)}
          feature={'limits.integrations'}
          onClose={() => setModal(null)}
          planID={planID}
          show
        />
      )}
    </Card>
  );
};

export default AdminIntegrationPlanCard;
